import {
  Grid,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { Close } from '@mui/icons-material';
import { Autocomplete } from '@material-ui/lab';
import { fromJS, List as ImmutableList } from 'immutable';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DonationProvider } from '@joindeed/calculate-fees';

import { selectUser } from 'entities/user/selectors';
import FormTextFieldRedesign from 'retired/FormTextFieldRedesign';
import LocationSelectorRedesign from 'retired/LocationSelectorRedesign';
import OrganizationApi from 'entities/organization/api';
import Organization from 'entities/organization/model';
import { purple, purpleish } from 'utils/color-styles';
import { computeDonationProviderForNonprofit } from 'utils/computeDonationProviderForNonprofit';
import { useDebounce } from 'utils/useDebounce';
import { renderAddress } from 'utils/renderAddress';
import { getDonationProviderDisplayName } from 'utils/getDonationProviderDisplayName';
import { Privileges } from 'utils/privileges';

import Option from './Option';

const isValidName = (name: string): boolean => name != null && name.trim().length > 0;

const FormNonprofitsSelectorRedesign = ({
  fields,
  meta,
  allowSelection = true,
  allowDeselection = true,
  forFundraiser = false,
  acceptsDonations,
  initialLocation = null,
  required = false,
  limit = null,
  supportedCountries = null,
  getDisplayDonationProviderForNonprofit = computeDonationProviderForNonprofit,
  donationProvider,
  excludeDonationProviders,
  variant = 'outlined',
  fullWidth = false,
  inputComponent: InputComponent = FormTextFieldRedesign,
  locationSelectorLabel,
  locationSelectorPlaceholder,
  locationSelectorHelperText = locationSelectorPlaceholder,
  nonprofitSelectorLabel = 'Nonprofit name',
  nonprofitSelectorPlaceholder = 'Search by nonprofit name or EIN',
  nonprofitSelectorHelperText = nonprofitSelectorPlaceholder,
  nonprofitSelectorNoOptions = 'No nonprofits found',
  filterOptions = (options) => options,
}) => {
  const [locationSelectorTouched, setLocationSelectorTouched] = useState(false);
  const [nonprofitsSelectorTouched, setNonprofitsSelectorTouched] = useState(false);
  const [options, setOptions] = useState(fromJS([]));
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const user = useSelector(selectUser);
  const preferStripe = user?.organization?.settings?.get('preferStripe') || false;

  const showPoweredBy = user?.isAdmin || false;
  const isOutlined = variant === 'outlined';

  const debouncedSearchTerm = useDebounce(searchTerm, 333);

  const fetchOptions = () => {
    if (debouncedSearchTerm.length > 2) {
      setLoading(true);

      const searchNonprofitDB = user?.organization?.settings?.get('searchNonprofitDB') || false;

      OrganizationApi.searchNonprofits(
        {
          searchTerm: debouncedSearchTerm,
          countryCode: selectedLocation,
          includeBlocked: user?.hasPrivilege(Privileges.NONPROFITS_UNBLOCK),
          acceptsDonations,
          donationProvider,
          excludeDonationProviders,
        },
        searchNonprofitDB
      ).subscribe((results) => {
        setOptions(fromJS(filterOptions(results)));
        setLoading(false);
      });
    } else {
      setOptions(fromJS([]));
    }
  };

  useEffect(() => {
    fetchOptions();
  }, [debouncedSearchTerm, selectedLocation]);

  const validateSelections = (selectedList: ImmutableList<Organization>): boolean => {
    if (selectedList.size === 0) {
      return false;
    }

    const valid = selectedList.every((value: Organization | undefined) => {
      if (value === undefined) {
        return false;
      }

      const { currencyCode = '', locationObject } = (value?.toJS() || {}) as Organization;

      if ((locationObject && locationObject.countryCode === '') || currencyCode === '') {
        return false;
      }

      return true;
    });

    return valid;
  };

  const addNew = (item: Organization) => {
    setNonprofitsSelectorTouched(true);
    const currentlySelectedCount = fields.length as number;
    const selectedNonprofits =
      fields.length === 0 ? [] : (fields.getAll().toArray() as Organization[]);

    if (forFundraiser && currentlySelectedCount) {
      if (
        selectedNonprofits.every(
          (nonprofit: Organization) =>
            computeDonationProviderForNonprofit(nonprofit, preferStripe) === DonationProvider.PayPal
        )
      ) {
        // eslint-disable-next-line no-alert
        alert('PayPal only allows to select 1 nonprofit per fundraiser');
      } else if (
        selectedNonprofits.every(
          (nonprofit: Organization) =>
            computeDonationProviderForNonprofit(nonprofit, preferStripe) ===
            DonationProvider.GlobalGiving
        )
      ) {
        // eslint-disable-next-line no-alert
        alert('GlobalGiving only allows to select 1 nonprofit per fundraiser');
      } else if (
        selectedNonprofits.every(
          (nonprofit: Organization) =>
            computeDonationProviderForNonprofit(nonprofit, preferStripe) === DonationProvider.Stripe
        )
      ) {
        // eslint-disable-next-line no-alert
        alert('Stripe only allows to select 1 nonprofit per fundraiser');
      } else if (
        ![...selectedNonprofits, item].every(
          (nonprofit: Organization) =>
            computeDonationProviderForNonprofit(nonprofit, preferStripe) === DonationProvider.NFG
        )
      ) {
        // eslint-disable-next-line no-alert
        alert(
          'You may only select multiple nonprofits for a fundraiser that is powered by Network For Good'
        );
      }
    }

    if (validateSelections(ImmutableList.of(item))) {
      (fields as ImmutableList<Organization>).push(item);
    }
  };

  const onNonprofitListChange = (event: any, nonprofit: string | Organization): string | void => {
    const nonprofitState = fields.getAll() || [];
    const currentlySelected = [...nonprofitState, nonprofit as Organization];
    const currentlySelectedCount = currentlySelected.length;

    if (currentlySelectedCount > (limit as unknown as number)) {
      if (!Number.isNaN(limit as unknown as number) && Number(limit) > 1) {
        alert(`You cannot select more than ${limit} nonprofits`);
      }
      alert('You can only select a single nonprofit');

      // eslint-disable-next-line no-useless-return, consistent-return
      return;
    }

    addNew(nonprofit as Organization);
    setOptions(fromJS([]));

    // eslint-disable-next-line no-useless-return, consistent-return
    return;
  };

  return (
    <Grid>
      {allowSelection && (
        <>
          <Grid container spacing={3}>
            <Grid item md={fullWidth ? 12 : 6}>
              <LocationSelectorRedesign
                required={required}
                onSelect={(location) => {
                  setSelectedLocation(location);
                  setLocationSelectorTouched(true);
                }}
                onBlur={() => setLocationSelectorTouched(true)}
                variant={variant}
                inputComponent={InputComponent}
                passedCountries={supportedCountries}
                initialLocation={initialLocation}
                label={locationSelectorLabel}
                placeholder={locationSelectorPlaceholder}
                helperText={locationSelectorHelperText}
                meta={{
                  touched: nonprofitsSelectorTouched || locationSelectorTouched,
                  error: selectedLocation === null || !selectedLocation || undefined,
                }}
              />
            </Grid>
            <Grid item md={fullWidth ? 12 : 6}>
              <Autocomplete
                openOnFocus={Boolean(searchTerm)}
                disableClearable
                noOptionsText={nonprofitSelectorNoOptions}
                onChange={onNonprofitListChange}
                onOpen={() => fetchOptions()}
                getOptionLabel={(option) =>
                  option !== nonprofitSelectorNoOptions ? option.get('name') : ''
                }
                loading={loading}
                placeholder={nonprofitSelectorPlaceholder}
                renderInput={(params) => (
                  <InputComponent
                    {...params}
                    required={required}
                    label={nonprofitSelectorLabel}
                    onBlur={() => setNonprofitsSelectorTouched(true)}
                    onFocus={() => setNonprofitsSelectorTouched(false)}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                    }}
                    variant={variant}
                    InputLabelProps={isOutlined ? { shrink: true } : null}
                    meta={meta}
                    alwaysDisplayErrors={nonprofitsSelectorTouched}
                    disabled={limit < fields.size}
                    helperText={nonprofitSelectorHelperText}
                  />
                )}
                renderOption={(option) => {
                  // @NOTE-RT: This is the only way to have clickable elements in your `nonprofitSelectorNoOptions` node per https://stackoverflow.com/a/63806699
                  if (!option || option === nonprofitSelectorNoOptions) {
                    return nonprofitSelectorNoOptions;
                  }

                  return (
                    <Option
                      nonprofit={option}
                      key={option.get('id') || 0}
                      getDisplayDonationProviderForNonprofit={
                        getDisplayDonationProviderForNonprofit
                      }
                    />
                  );
                }}
                variant={variant}
                PaperComponent={(paperProps) => <Paper {...paperProps} square={!isOutlined} />}
                options={options?.toArray() ?? []}
                filterOptions={(nonprofitSelectorOptions) => {
                  if (loading || !searchTerm) {
                    return [];
                  }

                  if (nonprofitSelectorOptions.length > 0) {
                    return nonprofitSelectorOptions;
                  }

                  // @NOTE-RT: This is the only way to have clickable elements in your `nonprofitSelectorNoOptions` node per https://stackoverflow.com/a/63806699
                  return [nonprofitSelectorNoOptions];
                }}
              />
            </Grid>
          </Grid>
        </>
      )}
      <List style={{ maxHeight: 560, overflow: 'auto' }}>
        {fields.length > 0 &&
          fields.getAll().map((nonprofit: Organization, index: number) => {
            if (!nonprofit) {
              return null;
            }
            const { locationObject, id } = nonprofit.toJS();

            const location = renderAddress(locationObject);

            if (location === '') {
              return null;
            }

            const displayDonationProviderCode = getDisplayDonationProviderForNonprofit(
              nonprofit,
              false
            );
            const displayDonationProviderName = getDonationProviderDisplayName(
              displayDonationProviderCode
            );

            return (
              <ListItem
                key={id}
                style={{
                  backgroundColor: purpleish,
                  borderRadius: isOutlined ? 16 : 0,
                }}
              >
                <ListItemText
                  primary={
                    <div style={{ display: 'flex' }}>
                      <div style={{ color: purple }}>{nonprofit.get('name')}</div>{' '}
                      {nonprofit.get('ein') ? <>&nbsp;–&nbsp;EIN: {nonprofit.get('ein')}</> : null}
                    </div>
                  }
                  secondary={
                    <>
                      {location}
                      {showPoweredBy &&
                        displayDonationProviderName &&
                        isValidName(displayDonationProviderName) && (
                          <>
                            <br />
                            Powered by: {displayDonationProviderName}
                          </>
                        )}
                    </>
                  }
                />
                {allowDeselection && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => {
                        fields.remove(index);
                      }}
                    >
                      <Close />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            );
          })}
      </List>
    </Grid>
  );
};

export default FormNonprofitsSelectorRedesign;
